import * as THREE from 'three';
import { SurfaceType } from '../../terrain/SurfaceType';
import { calibrateLaunchConditions } from '../LaunchCalibration';
import { applyLieEffects } from '../LieEffects';
import type { TerrainSurfaceProbe } from '../TerrainSurfaceProbe';
import { trajectoryForClub } from '../Trajectory';
import type { AiSkillProfile } from './AiGolfer';
import type { AiShotPlan } from './AiShotPlanner';
import { PuttLaunchSolver } from '../PuttLaunchSolver';

export interface AiExecutionErrors {
  launchDirectionErrorRadians: number;
  clubSpeedError: number;
  facePathErrorRadians: number;
  strikeQuality: number;
  puttingPaceError: number;
  puttingLineErrorRadians: number;
}
export interface AiLaunchIntent {
  readonly velocity: THREE.Vector3;
  readonly spin: THREE.Vector3;
  readonly putt: boolean;
  readonly errors: AiExecutionErrors;
  readonly aimRadians: number;
  readonly power: number;
  readonly options?: {
    readonly rolloutResistanceMultiplier?: number;
    readonly bounceRestitutionMultiplier?: number;
  };
}

export class AiExecutionModel {
  private readonly puttLaunchSolver = new PuttLaunchSolver();
  createLaunch(
    ballPosition: THREE.Vector3,
    plan: AiShotPlan,
    skill: AiSkillProfile,
    _lie: SurfaceType,
    probe: TerrainSurfaceProbe,
    seed = Math.random(),
  ): AiLaunchIntent {
    const toTarget = plan.target.clone().sub(ballPosition).setY(0);
    const aimRadians = Math.atan2(toTarget.x, toTarget.z);
    const r = this.rng(seed);
    const sigma = plan.dispersionRadiusMeters / Math.max(20, toTarget.length());
    const errors: AiExecutionErrors = {
      launchDirectionErrorRadians: this.normal(r) * sigma,
      clubSpeedError: this.normal(r) * (0.1 * (1 - skill.consistency / 115)),
      facePathErrorRadians: this.normal(r) * sigma * 0.7,
      strikeQuality: THREE.MathUtils.clamp(
        0.62 + skill.consistency / 190 + this.normal(r) * 0.09,
        0.45,
        1.05,
      ),
      puttingPaceError: this.normal(r) * (0.18 * (1 - skill.putting / 125)),
      puttingLineErrorRadians: this.normal(r) * sigma * 0.55,
    };
    if (plan.club.id === 'putter') {
      const puttingSigma =
        (plan.dispersionRadiusMeters / Math.max(1, toTarget.length())) *
        (1.05 - skill.putting / 220);
      const puttingLineError = THREE.MathUtils.clamp(this.normal(r) * puttingSigma, -0.035, 0.035);
      const puttingPaceError = THREE.MathUtils.clamp(
        this.normal(r) * (0.055 * (1 - skill.putting / 140)),
        -0.045,
        0.055,
      );
      errors.puttingLineErrorRadians = puttingLineError;
      errors.puttingPaceError = puttingPaceError;
      const intendedRollMeters =
        (plan.intendedRollDistanceMeters ?? toTarget.length()) * (1 + puttingPaceError);
      return {
        velocity: this.puttLaunchSolver.solveForDistance(
          intendedRollMeters,
          aimRadians,
          0,
          puttingLineError,
        ),
        spin: new THREE.Vector3(),
        putt: true,
        errors,
        aimRadians,
        power: plan.power,
      };
    }
    const trajectory = trajectoryForClub(plan.club, plan.trajectory);
    const base = calibrateLaunchConditions({
      club: plan.club,
      power: THREE.MathUtils.clamp(plan.power * (1 + errors.clubSpeedError), 20, 100),
      quality: errors.strikeQuality / trajectory.difficulty,
      aimRadians: aimRadians + errors.launchDirectionErrorRadians,
      faceErrorRadians: errors.facePathErrorRadians,
      trajectory,
      attackLaunchDeltaDegrees: errors.strikeQuality < 0.58 ? -4 : 0,
    });
    const surface = probe.sample(ballPosition.x, ballPosition.z).properties;
    const launch = applyLieEffects(base, surface, aimRadians);
    const backspin = (launch.backspinRpm * Math.PI * 2) / 60;
    return {
      velocity: launch.velocity,
      spin: new THREE.Vector3(
        -backspin,
        -errors.facePathErrorRadians * plan.club.spin.sidespinSensitivity * 95,
        0,
      ),
      putt: false,
      errors,
      aimRadians,
      power: plan.power,
      options: {
        rolloutResistanceMultiplier: launch.rolloutResistanceMultiplier,
        bounceRestitutionMultiplier: launch.bounceRestitutionMultiplier,
      },
    };
  }
  private rng(seed: number): () => number {
    let s = Math.floor(seed * 2147483647) || 1;
    return () => (s = (s * 48271) % 2147483647) / 2147483647;
  }
  private normal(rand: () => number): number {
    const u = Math.max(1e-6, rand());
    const v = Math.max(1e-6, rand());
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  }
}
